import { z } from 'zod';

export const listSearchSchema = z.object({
  q: z.string().optional(),
});

const NO_SEARCH_MATCH_MESSAGE = 'Nothing matches the search';

const NO_SEARCH_MATCH_HINT = 'Check the spelling, or clear the search to see the whole list.';

interface TableEmptyText {
  emptyMessage: string;
  emptyHint: string | undefined;
}

export function getTableEmptyText(
  searchText: string | undefined,
  message: string,
  hint?: string,
): TableEmptyText {
  if (searchText) return { emptyMessage: NO_SEARCH_MATCH_MESSAGE, emptyHint: NO_SEARCH_MATCH_HINT };
  return { emptyMessage: message, emptyHint: hint };
}

export type SearchableValue = string | null | undefined;

const HYPHENS = /-/g;

const withoutHyphens = (text: string): string => text.replace(HYPHENS, '');

export function matchesSearchText(text: string, query: string): boolean {
  const haystack = text.toLowerCase();
  const needle = query.trim().toLowerCase();
  return haystack.includes(needle) || withoutHyphens(haystack).includes(withoutHyphens(needle));
}

export interface HighlightSegment {
  start: number;
  text: string;
  isMatch: boolean;
}

interface MatchRange {
  start: number;
  end: number;
}

function findHyphenFreeMatches(text: string, query: string): MatchRange[] {
  const needle = withoutHyphens(query.trim().toLowerCase());
  if (needle === '') return [];

  const sourceIndexes: number[] = [];
  let hyphenFreeText = '';
  for (let index = 0; index < text.length; index += 1) {
    const char = text.charAt(index);
    if (char === '-') continue;
    const lowerChar = char.toLowerCase();
    hyphenFreeText += lowerChar;
    sourceIndexes.push(...Array.from(lowerChar, () => index));
  }

  const ranges: MatchRange[] = [];
  let found = hyphenFreeText.indexOf(needle);
  while (found !== -1) {
    const start = sourceIndexes.at(found);
    const last = sourceIndexes.at(found + needle.length - 1);
    if (start !== undefined && last !== undefined) ranges.push({ start, end: last + 1 });
    found = hyphenFreeText.indexOf(needle, found + needle.length);
  }
  return ranges;
}

export function toHighlightSegments(text: string, query: string): HighlightSegment[] {
  const segments: HighlightSegment[] = [];
  let cursor = 0;
  for (const range of findHyphenFreeMatches(text, query)) {
    if (range.start > cursor) {
      segments.push({ start: cursor, text: text.slice(cursor, range.start), isMatch: false });
    }
    segments.push({ start: range.start, text: text.slice(range.start, range.end), isMatch: true });
    cursor = range.end;
  }
  if (cursor < text.length) {
    segments.push({ start: cursor, text: text.slice(cursor), isMatch: false });
  }
  return segments;
}

export function filterRowsBySearch<TRow>(
  rows: TRow[] | undefined,
  appliedText: string | undefined,
  toSearchableValues: (row: TRow) => SearchableValue[],
): TRow[] {
  const allRows = rows ?? [];
  const needle = appliedText?.trim();
  if (!needle) return allRows;
  return allRows.filter((row) =>
    matchesSearchText(toSearchableValues(row).filter(Boolean).join(' '), needle),
  );
}
